import type { Enums } from '@/types/supabase';
import { brand } from '@/lib/branding';

// --- Anomalies Trackdechets ---
// Refonte D-ANOM-01 (2026-05-16) : 2 familles d'anomalies.
//  - Famille 1 (divergences de champ) : sens + champ obligatoires, details non null.
//  - Famille 2 (evenements de cycle de vie) : sens + champ + details a null, definitives.

export type AnomalieType =
  | 'EMETTEUR_MODIFIE' | 'WORKER_MODIFIE' | 'CENTRE_MODIFIE' | 'ICPE_MODIFIE'
  | 'CODE_DECHET_MODIFIE' | 'CAP_DIVERGENT' | 'OPERATION_MODIFIEE'
  | 'FLUX_ICPE_MODIFIE' | 'TRANSPORTEUR_MODIFIE' | 'COURTIER_MODIFIE'
  | 'STATUT_DRAFT_APRES_PUBLICATION' | 'STATUT_INCONNU_TD'
  | 'PROPAGATION_ECHEC'
  | 'REFUSE' | 'ANNULE_TIERS' | 'SUPPRIME_SUR_TD' | 'INACCESSIBLE_TD';

export type AnomalieSens = 'td_vers_app' | 'app_vers_td';
export type AnomalieResolvedBy = 'auto-sync' | 'auto-propagation' | 'superseded';

export const FAMILLE_2_TYPES: ReadonlySet<AnomalieType> = new Set([
  'REFUSE', 'ANNULE_TIERS', 'SUPPRIME_SUR_TD',
] as const);

export const ANOMALIES_PROPAGEABLES: ReadonlySet<AnomalieType> = new Set([
  'CENTRE_MODIFIE',
  'ICPE_MODIFIE',
  'EMETTEUR_MODIFIE',
  'WORKER_MODIFIE',
  'CODE_DECHET_MODIFIE',
  'CAP_DIVERGENT',
  'COURTIER_MODIFIE',
  'TRANSPORTEUR_MODIFIE',
  'PROPAGATION_ECHEC',
] as const);

export interface AnomalieDetails {
  valeur_app: string | number | null;
  valeur_td: string | number | null;
  raison?: string;
}

export interface Anomalie {
  type: AnomalieType;
  sens: AnomalieSens | null;
  champ: string | null;
  detected_at: string;
  details: AnomalieDetails | null;
  resolved_at: string | null;
  resolved_by: AnomalieResolvedBy | null;
}

export function classifyAnomalie(a: Anomalie): 'resolue' | 'definitive' | 'ouverte_corrigeable' {
  if (a.resolved_at != null) return 'resolue';
  if (FAMILLE_2_TYPES.has(a.type)) return 'definitive';
  return 'ouverte_corrigeable';
}

// C14A-10 : la synchro stocke les valeurs booleennes (flux ICPE) en texte « true » / « false ».
export function formatValeurAnomalie(v: string | number | null | undefined): string {
  if (v == null) return '-';
  if (v === 'true') return 'Oui';
  if (v === 'false') return 'Non';
  return String(v);
}

// Sous-ensemble du query builder PostgREST, commun a la table bordereaux et a v_bordereaux_search.
interface FiltrablePostgrest {
  filter(column: string, operator: string, value: unknown): this;
  not(column: string, operator: string, value: unknown): this;
}

// Pendant PostgREST de classifyAnomalie === 'ouverte_corrigeable' : au moins une anomalie ouverte, aucune de
// famille 2, BSD present sur TD. Grille /operations/bordereaux et compteurs (tableau de bord, Supervision).
export function filtrerAnomaliesCorrigeables<Q extends FiltrablePostgrest>(query: Q): Q {
  let q = query.filter('deleted_on_td', 'eq', false).filter('anomalies', 'cs', '[{"resolved_at":null}]');
  for (const type of FAMILLE_2_TYPES) q = q.not('anomalies', 'cs', `[{"type":"${type}"}]`);
  return q;
}

/**
 * Tonnage reellement facturable d'un BSD apres refus eventuel.
 * - Si tonnage reel (TD reception) present : tonnage - (tonnage_refuse ?? 0)
 * - Sinon fallback sur quantite_estimee (BSD pas encore recu)
 */
export function tonnageFacturable(bsd: {
  tonnage?: number | null;
  tonnageRefuse?: number | null;
  quantiteEstimee?: number | null;
}): number | null {
  if (bsd.tonnage != null) {
    return bsd.tonnage - (bsd.tonnageRefuse ?? 0);
  }
  return bsd.quantiteEstimee ?? null;
}

export const ANOMALIE_LABELS: Record<AnomalieType, string> = {
  EMETTEUR_MODIFIE: 'Producteur modifié sur Trackdechets',
  WORKER_MODIFIE: 'Entreprise de travaux modifiée sur Trackdechets',
  CENTRE_MODIFIE: 'Centre de traitement modifié sur Trackdechets',
  ICPE_MODIFIE: 'Installation ICPE modifiée sur Trackdechets',
  CODE_DECHET_MODIFIE: 'Code déchet modifié sur Trackdechets',
  CAP_DIVERGENT: 'Numéro CAP différent sur Trackdechets',
  OPERATION_MODIFIEE: 'Code opération modifié sur Trackdechets',
  FLUX_ICPE_MODIFIE: 'Flux ICPE modifié sur Trackdechets',
  TRANSPORTEUR_MODIFIE: 'Transporteur modifié sur Trackdechets',
  COURTIER_MODIFIE: 'Courtier modifié sur Trackdechets',
  STATUT_DRAFT_APRES_PUBLICATION: 'BSD repassé en brouillon après publication sur Trackdechets',
  STATUT_INCONNU_TD: 'Statut Trackdechets inconnu de l\'application',
  PROPAGATION_ECHEC: `Modification ${brand.name} non appliquée sur Trackdechets`,
  REFUSE: 'BSD refusé par le centre de traitement',
  ANNULE_TIERS: 'BSD annulé sur Trackdechets',
  SUPPRIME_SUR_TD: 'BSD supprimé sur Trackdechets',
  INACCESSIBLE_TD: `BSD inaccessible sur Trackdechets (${brand.name} n'y figure plus)`,
};

// Libelles humains par champ canonique d'anomalie (cle = valeur de Anomalie.champ
// posee par les services). Cf. ANOM-R09 dans PRODUIT/domaine/14_anomalies.md
// (section "Champ canonique") pour la liste autoritaire.
// Alias historiques conserves pour eviter une migration des anomalies existantes :
//  - 'transporter.company' = 'transporteur'
//  - 'cap:numero' = 'cap.numero'
//  - 'cap:centre' = 'centre' (legacy pre-refonte D-ANOM-01, plus produit par le code actuel)
export const CHAMP_LABELS: Record<string, string> = {
  centre: 'Centre de traitement',
  'cap:centre': 'Centre de traitement',
  icpe: 'Installation ICPE',
  producteur: 'Producteur',
  contact_producteur: 'Contact producteur',
  entreprise_travaux: 'Entreprise de travaux',
  contact_entreprise_travaux: 'Contact entreprise de travaux',
  code_dechet: 'Code déchet',
  'cap.numero': 'Numéro CAP',
  'cap:numero': 'Numéro CAP',
  courtier: 'Courtier',
  code_operation: 'Code opération',
  flux_icpe: 'Flux ICPE',
  transporteur: 'Transporteur',
  'transporter.company': 'Transporteur',
  statut: 'Statut',
  statut_inconnu: 'Statut',
  chantier: 'Chantier',
  nom: 'Nom du chantier',
  adresse: 'Adresse du chantier',
  wasteName: 'Nom du déchet',
  acces_td: 'Accès Trackdechets',
};

// Forme avec determinant pour injection dans le libelle PROPAGATION_ECHEC enrichi
// ("Mise a jour du centre non appliquee sur Trackdechets"). Separe de CHAMP_LABELS
// pour rester un Record<string,string> simple, idiomatique et facile a grep.
const CHAMP_LABELS_PREFIXED: Record<string, string> = {
  centre: 'du centre',
  'cap:centre': 'du centre',
  icpe: "de l'installation ICPE",
  producteur: 'du producteur',
  contact_producteur: 'du contact producteur',
  entreprise_travaux: "de l'entreprise de travaux",
  contact_entreprise_travaux: "du contact entreprise de travaux",
  code_dechet: 'du code déchet',
  'cap.numero': 'du numéro CAP',
  'cap:numero': 'du numéro CAP',
  courtier: 'du courtier',
  code_operation: 'du code opération',
  flux_icpe: 'du flux ICPE',
  transporteur: 'du transporteur',
  'transporter.company': 'du transporteur',
  statut: 'du statut',
  chantier: 'du chantier',
  nom: 'du nom du chantier',
  adresse: "de l'adresse du chantier",
  wasteName: 'du nom du déchet',
};

/** Libelle humain du champ canonique d'une anomalie (null si famille 2 ou champ absent). */
export function getChampLabel(champ: string | null): string | null {
  if (!champ) return null;
  return CHAMP_LABELS[champ] ?? champ;
}

/**
 * Libelle d'affichage d'une anomalie.
 *  - PROPAGATION_ECHEC avec champ connu : "Mise a jour <prefixed> non appliquee sur Trackdechets".
 *  - Tout autre cas : fallback sur ANOMALIE_LABELS[type].
 */
export function getAnomalieLabel(a: Anomalie): string {
  if (a.type === 'PROPAGATION_ECHEC' && a.champ) {
    const prefixed = CHAMP_LABELS_PREFIXED[a.champ];
    if (prefixed) return `Mise à jour ${prefixed} non appliquée sur Trackdechets`;
  }
  return ANOMALIE_LABELS[a.type] ?? a.type;
}

// --- Enums ---

export type TypeTracabilite = Enums<'type_tracabilite'>;
export type StatutAcceptation = 'ACCEPTED' | 'PARTIALLY_REFUSED' | 'REFUSED';

const STATUTS_ACCEPTATION_VALIDES: ReadonlySet<string> = new Set(['ACCEPTED', 'PARTIALLY_REFUSED', 'REFUSED']);

export function parseStatutAcceptation(raw: string | null | undefined): StatutAcceptation | null {
  if (raw && STATUTS_ACCEPTATION_VALIDES.has(raw)) return raw as StatutAcceptation;
  return null;
}
// Statut = valeur brute de Trackdechets (source de verite)
// BSDA: INITIAL, SIGNED_BY_PRODUCER, SIGNED_BY_WORKER, SENT, RECEIVED, PROCESSED, REFUSED, CANCELED, AWAITING_CHILD
// BSDD: DRAFT, SEALED, SIGNED_BY_PRODUCER, SENT, RECEIVED, ACCEPTED, PROCESSED, REFUSED, CANCELED, TEMP_STORED, RESEALED, RESENT...
export type StatutBordereau = string;
export type OriginBordereau = Enums<'origin_bordereau'>;

// --- Row type (snake_case, BDD) ---

export interface BordereauRow {
  id: string;
  created_at: string;
  updated_at: string;
  type_tracabilite: TypeTracabilite;
  statut: StatutBordereau;
  origin: OriginBordereau;
  numero_td: string | null;
  td_id: string | null;
  tonnage: number | null;
  nb_colis: number | null;

  quantite_estimee: number | null;
  code_operation: string | null;
  numero_cap_td: string | null;
  packagings: Array<{ code: string; quantity: number; other?: string }> | null;
  is_draft: boolean;
  is_reshipment: boolean;
  source_bsd_ids: string[] | null;
  donnees_td: Record<string, unknown> | null;
  last_synced_at: string | null;
  needs_review: boolean;
  cap_id: string;
  transport_id: string | null;
  deleted_on_td: boolean;
  anomalies: Anomalie[] | null;
  tonnage_refuse: number | null;
  statut_acceptation: string | null;
  motif_refus: string | null;
  // Joins
  cap?: {
    id: string;
    reference: string | null;
    chantier_id: string;
    centre_id: string | null;
    type_dechet_id: string;
    groupe: string;
    is_icpe: boolean;
    icpe_siret: string | null;
    icpe_name: string | null;
    icpe_address: string | null;
    icpe_contact_nom: string | null;
    icpe_contact_telephone: string | null;
    icpe_contact_email: string | null;
    numero_cap: string | null;
    nom_dechet: string | null;
    chantiers?: { etablissement_id: string; reference_interne: string | null; nom: string | null } | null;
    types_dechet?: { nom: string; regime: string; code_ced: string | null; consistence: string | null; integrite: string | null; famille: number | null } | null;
    centres_traitement?: { raison_sociale: string; alias: string | null; siret: string | null; adresse_complete: string | null } | null;
  } | null;
}

// --- Frontend type (camelCase) ---

export interface Bordereau {
  id: string;
  createdAt: string;
  updatedAt: string;
  typeTracabilite: TypeTracabilite;
  statut: StatutBordereau;
  origin: OriginBordereau;
  numeroTd: string | null;
  tdId: string | null;
  tonnage: number | null;
  nbColis: number | null;

  quantiteEstimee: number | null;
  codeOperation: string | null;
  numeroCapTd: string | null;
  packagings: Array<{ code: string; quantity: number; other?: string }> | null;
  isDraft: boolean;
  isReshipment: boolean;
  sourceBsdIds: string[] | null;
  donneesTd: Record<string, unknown> | null;
  lastSyncedAt: string | null;
  needsReview: boolean;
  capId: string;
  transportId: string | null;
  deletedOnTd: boolean;
  anomalies: Anomalie[];
  tonnageRefuse: number | null;
  statutAcceptation: StatutAcceptation | null;
  motifRefus: string | null;
  // From joins
  typeDechetId: string | null;
  /** D-CAP-GROUPE : groupe du CAP porteur, entree du lookup ADR (CAP-R29). */
  capGroupe: string | null;
  capReference: string | null;
  /** RC4.09 : nom du dechet saisi sur le CAP (envoye a Trackdechets) ; vide = repli sur le type. */
  nomDechet: string | null;
  typeDechetNom: string | null;
  typeDechetRegime: string | null;
  typeDechetCodeCed: string | null;
  centreId: string | null;
  centreNom: string | null;
  centreSiret: string | null;
  centreAdresse: string | null;
  isIcpe: boolean;
  icpeSiret: string | null;
  icpeName: string | null;
  icpeAddress: string | null;
  // TR-R34 : contact de l'ICPE, pre-rempli sur le lieu de livraison du 1er trajet.
  icpeContactNom: string | null;
  icpeContactTelephone: string | null;
  icpeContactEmail: string | null;
  chantierId: string | null;
  chantierReferenceInterne: string | null;
  chantierNom: string | null;
  numeroCap: string | null;
}

// --- Input types ---

export interface PackagingLigne {
  conditionnementCode: string;
  nombreColis: number;
  descriptionAutre?: string;
}

export interface CreateBsdInput {
  capId: string;
  packagings: PackagingLigne[];
  quantiteEstimee: number;
  publish?: boolean;
  numeroScelle?: string;
  /**
   * Cle d'idempotence (UUID). Generee une fois par tentative utilisateur, stable
   * sur les retries React Query. Evite les doublons sur Trackdechets en cas de
   * timeout HTTP entre le succes TD et le retour Edge Function.
   */
  idempotencyKey?: string;
}

export interface UpdateBsdInput {
  packagings?: PackagingLigne[];
  quantiteEstimee?: number;
  numeroScelle?: string;
}

// --- Labels statut TD pour affichage ---

// Labels alignes sur l'affichage de l'interface web Trackdechets
// Note : INITIAL (BSDA) et DRAFT (BSDD) peuvent etre "Brouillon" ou "Initial"/"Publie"
// selon isDraft. Utiliser getBsdStatutLabel() pour le label correct.
export const STATUT_BSD_LABELS: Record<string, string> = {
  INITIAL: 'Initial',             // BSDA : si isDraft=true -> "Brouillon", sinon "Initial"
  DRAFT: 'Brouillon',             // BSDD : toujours brouillon
  SEALED: 'Publié',               // BSDD : finalise = "Publie" sur TD
  SIGNED_BY_PRODUCER: 'Signé par Producteur',
  SIGNED_BY_WORKER: 'Signé par Entreprise travaux',
  SENT: 'Signé par Transporteur',
  RECEIVED: 'Reçu',
  ACCEPTED: 'Accepté',
  PROCESSED: 'Traité',
  REFUSED: 'Refusé',
  CANCELED: 'Annulé',
  AWAITING_CHILD: 'En attente suite',
  TEMP_STORED: 'Entreposé',
  TEMP_STORER_ACCEPTED: 'Accepté (entreposage)',
  RESEALED: 'Reconditionné',
  SIGNED_BY_TEMP_STORER: 'Signé entreposeur',
  RESENT: 'Réexpédié',
  FOLLOWED_WITH_PNTTD: 'Suivi hors France',
  AWAITING_GROUP: 'En attente regroupement',
  GROUPED: 'Regroupé',
  NO_TRACEABILITY: 'Perte traçabilité',
};

// Statuts TD proposes dans les filtres de statut (Brouillon et Publie a part) : grille Bordereaux de
// /operations et liste des BSD orphelins (RC3.84).
export const FILTRE_STATUTS_BSD = [
  'SIGNED_BY_PRODUCER', 'SIGNED_BY_WORKER', 'SENT', 'RECEIVED', 'ACCEPTED', 'PROCESSED', 'REFUSED', 'CANCELED',
] as const;

/** Retourne le label statut correct en tenant compte de isDraft.
 * BSDA INITIAL + isDraft=true = "Brouillon", BSDA INITIAL + isDraft=false = "Publie"
 * BSDD DRAFT = "Brouillon", BSDD SEALED = "Publie"
 */
export function getBsdStatutLabel(statut: string, isDraft: boolean): string {
  if (statut === 'INITIAL') return isDraft ? 'Brouillon' : 'Publié';
  return STATUT_BSD_LABELS[statut] ?? statut;
}

/** RC3.56 / TR-R34 : centre de traitement d'un BSD a la selection pour un transport.
 * En transit ICPE, le centre charge est l'exutoire final : on precise que le camion livre l'ICPE.
 */
export function libelleCentreBsd(bsd: Pick<Bordereau, 'centreNom' | 'isIcpe' | 'icpeName'>): string {
  const centre = bsd.centreNom ?? '';
  if (!bsd.isIcpe) return centre;
  const via = bsd.icpeName ? `via ICPE ${bsd.icpeName}` : 'via ICPE';
  return centre ? `${centre} (${via})` : `(${via})`;
}

/**
 * Chantier : - (revue client RC3.29)
 * Regle metier : TR-R34 (PRODUIT/domaine/06_transport.md)
 * Decision : D-ICPE-9B (amendement 2026-09-29)
 * Destination effective d'un BSD : le lieu ou le camion livre (l'ICPE si transit, sinon le centre).
 * Avec le chantier, c'est la cle d'une livraison. null = destination inconnue.
 */
export function destinationEffective(
  bsd: { isIcpe?: boolean; icpeSiret?: string | null; centreId?: string | null },
): string | null {
  if (bsd.isIcpe) return `icpe:${bsd.icpeSiret ?? ''}`;
  return bsd.centreId ?? null;
}

/** RC3.56 : libelle d'un BSD a l'etape Livraisons du formulaire transport (numero, centre, n° CAP). */
export function libelleBsdSelection(
  bsd: Pick<Bordereau, 'numeroTd' | 'centreNom' | 'isIcpe' | 'icpeName' | 'numeroCap'>,
): string {
  return [bsd.numeroTd, libelleCentreBsd(bsd), bsd.numeroCap ? `CAP ${bsd.numeroCap}` : null]
    .filter(Boolean)
    .join(' · ');
}

export const STATUT_BSD_COLORS: Record<string, string> = {
  INITIAL: 'blue',
  DRAFT: 'gray',
  SEALED: 'blue',
  SIGNED_BY_PRODUCER: 'blue',
  SIGNED_BY_WORKER: 'blue',
  SENT: 'emerald',
  RECEIVED: 'emerald',
  ACCEPTED: 'emerald',
  PROCESSED: 'green',
  REFUSED: 'red',
  CANCELED: 'red',
  // D-ICPE-FILTRE-STATUTS : dechet pris en charge par l'ICPE ou reparti, comme Recu / Accepte.
  AWAITING_CHILD: 'emerald',
  TEMP_STORED: 'emerald',
  TEMP_STORER_ACCEPTED: 'emerald',
  RESEALED: 'emerald',
  SIGNED_BY_TEMP_STORER: 'emerald',
  RESENT: 'emerald',
  AWAITING_GROUP: 'emerald',
  GROUPED: 'emerald',
  // Fins de suivi, comme Traite.
  FOLLOWED_WITH_PNTTD: 'green',
  NO_TRACEABILITY: 'green',
};

// Tailwind classes complètes (sinon le purge ne génère pas les règles avec interpolation `bg-${color}-500`)
export const STATUT_BSD_STYLE: Record<string, { dot: string; text: string; badge: string }> = {
  blue:    { dot: 'bg-blue-500',    text: 'text-blue-600',    badge: 'bg-blue-500 hover:bg-blue-500 text-white'       },
  gray:    { dot: 'bg-gray-500',    text: 'text-gray-600',    badge: 'bg-gray-500 hover:bg-gray-500 text-white'       },
  emerald: { dot: 'bg-emerald-500', text: 'text-emerald-600', badge: 'bg-emerald-500 hover:bg-emerald-500 text-white' },
  green:   { dot: 'bg-green-500',   text: 'text-green-600',   badge: 'bg-green-500 hover:bg-green-500 text-white'     },
  red:     { dot: 'bg-red-500',     text: 'text-red-600',     badge: 'bg-red-500 hover:bg-red-500 text-white'         },
};

// --- Regles de modification Trackdechets ---

// Statuts finaux TD (miroir de TD_FINAL_STATUTS des EF) : le BSD n'a plus de transport a faire.
export const STATUTS_TD_FINAUX = ['PROCESSED', 'REFUSED', 'CANCELED', 'FOLLOWED_WITH_PNTTD', 'NO_TRACEABILITY', 'GROUPED'];

// Statuts finaux : aucune modification directe possible.
// Statuts ICPE (entreposage provisoire BSDD + groupement) ajoutes : une fois le
// dechet pris en charge par l'ICPE, SERME ne modifie plus le BSD (D-ICPE-9B).
export const STATUTS_NON_MODIFIABLES = ['PROCESSED', 'REFUSED', 'CANCELED', 'AWAITING_CHILD', 'NO_TRACEABILITY', 'GROUPED', 'FOLLOWED_WITH_PNTTD', 'AWAITING_GROUP', 'TEMP_STORED', 'TEMP_STORER_ACCEPTED', 'RESEALED', 'SIGNED_BY_TEMP_STORER', 'RESENT'];

// BSDD : updateForm sur DRAFT et SEALED uniquement
export const BSDD_MODIFIABLES = ['DRAFT', 'SEALED'];

// BSDA : updateBsda de INITIAL a SIGNED_BY_WORKER
export const BSDA_MODIFIABLES = ['INITIAL', 'SIGNED_BY_PRODUCER', 'SIGNED_BY_WORKER'];

/**
 * Chantier : RC3.115 (PLAN/Backlog_revue_client_2026-09-24.md)
 * Regle metier : BSD-R08 (PRODUIT/domaine/05_bsd.md)
 * Decision : D-BSD-RATTACHABLES (PRODUIT/decisions/)
 * Statuts d'un BSD avant toute prise en charge par un transporteur : seuls rattachables a un 1er trajet (miroir de
 * reserver_bsd_transport), et ceux ou les scelles restent modifiables sur TD (plan de chargement).
 */
export const STATUTS_AVANT_PRISE_EN_CHARGE: Record<TypeTracabilite, readonly string[]> = {
  bsda: BSDA_MODIFIABLES,
  bsdd: ['DRAFT', 'SEALED', 'SIGNED_BY_PRODUCER'],
};

export function avantPriseEnCharge(bsd: { typeTracabilite: TypeTracabilite; statut: string }): boolean {
  return STATUTS_AVANT_PRISE_EN_CHARGE[bsd.typeTracabilite].includes(bsd.statut);
}

/**
 * Chantier : RC3.126 (PLAN/spec_transport_depuis_icpe.md)
 * Regle metier : TR-R41, TR-R42 (PRODUIT/domaine/06_transport.md)
 * Decision : D-TRANSPORT-ICPE-01 (PRODUIT/decisions/)
 * Statuts d'un BSDD chez l'ICPE (entreposage provisoire accepte), avant la signature du transporteur du 2e trajet :
 * il peut partir de l'ICPE (miroir de reserver_bsd_transport et check_rang_lien_transport).
 */
export const STATUTS_BSDD_CHEZ_ICPE: readonly string[] = ['TEMP_STORER_ACCEPTED', 'RESEALED', 'SIGNED_BY_TEMP_STORER'];

/**
 * Chantier : - (hors chantier structure)
 * Regle metier : -
 * Decision : D-ICPE-9B (PRODUIT/decisions/), D-ICPE-FILTRE-STATUTS (decisions_developpement.md)
 * Groupes de statuts ICPE du filtre de la grille Bordereaux de /operations. Un BSDA reexpedie reste en
 * AWAITING_CHILD jusqu'au traitement du bordereau suite : il reste « a l'ICPE ».
 */
export const STATUTS_BSD_A_L_ICPE: readonly string[] = [...STATUTS_BSDD_CHEZ_ICPE, 'TEMP_STORED', 'AWAITING_GROUP', 'AWAITING_CHILD'];
export const STATUTS_BSD_REPARTIS_ICPE: readonly string[] = ['RESENT', 'GROUPED'];

/** Verifie si un BSD peut etre modifie directement via updateForm/updateBsda */
export function isBsdEditable(statut: string, regime: TypeTracabilite): boolean {
  if (STATUTS_NON_MODIFIABLES.includes(statut)) return false;
  const modifiables = regime === 'bsda' ? BSDA_MODIFIABLES : BSDD_MODIFIABLES;
  return modifiables.includes(statut);
}

/**
 * Chantier : - (ticket orphelin non modifiable SERME)
 * Regle metier : BSD-R26 (PRODUIT/domaine/05_bsd.md), INT-TD-05 (13_integrations.md)
 * Decision : D-ANOM-04
 * Predicat « SERME peut-il modifier ce BSD sur Trackdechets ? ». Un BSD orphelin rattache
 * (`rattache_orphelin`) OU un 2nd BSDA de re-expedition ICPE (`imported_td_reshipment`, cree par
 * l'ICPE — D-ICPE-FACT-REEXP) a ete cree par un tiers : SERME n'en est pas le createur TD
 * (seulement transporteur/courtier), donc updateBsda/updateForm echoue (« n'existe pas »).
 * Ces BSD sont exclus de la propagation app_vers_td (pas de PROPAGATION_ECHEC) et signales
 * par un badge. Les autres origins (created, imported_td_duplicate) sont modifiables.
 */
export function isBsdModifiableBySerme(origin: OriginBordereau | null | undefined): boolean {
  return origin !== 'rattache_orphelin' && origin !== 'imported_td_reshipment';
}

/** Champ d'anomalie du transporteur : canonique, ou alias historique des PROPAGATION_ECHEC (D-ANOM-08). */
export function estChampTransporteur(champ: string | null | undefined): boolean {
  return champ === 'transporteur' || champ === 'transporter.company';
}

/**
 * Libelle du signal porte par un BSD non modifiable par SERME (D-ANOM-04). La reexpedition recoit tout de meme le
 * transporteur et les scelles de son transport suivant (Lot 14a, D-TRANSPORT-SUIVANT-01) ; l'orphelin tiers, le
 * transporteur quand SERME y est courtiere (D-ANOM-08).
 */
export function libelleNonModifiableSerme(origin: OriginBordereau | null | undefined): string {
  return origin === 'imported_td_reshipment'
    ? `Émis par l'ICPE : ${brand.name} n'y écrit que le transporteur et les scellés`
    : `Émis par un tiers : ${brand.name} n'y écrit que le transporteur`;
}

// --- Edge Function payload ---

export type TdAction = 'create' | 'update' | 'duplicate' | 'delete' | 'get' | 'pdf';

export interface TdRequest {
  action: TdAction;
  bordereauId?: string;
  tdId?: string;
  payload?: Record<string, unknown>;
}

export interface TdResponse {
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
  tdId?: string;
  numeroTd?: string;
  pdfUrl?: string;
}
